<template>
	<div class="col-12">
		<div class="calendar-container">
			<div class="calendar-header">
				<h3 class="calendar-title">
					<i class="fas fa-calendar-alt mr-2"></i>
					Calendario de actividades
				</h3>
				<div class="calendar-filters">
					<button 
						class="filter-btn" 
						:class="{ active: filters.anniversaries, 'filter-anniversary': true }"
						@click="toggleFilter('anniversaries')"
					>
						<i class="fas fa-award"></i> Aniversarios
					</button>
					<button 
						class="filter-btn" 
						:class="{ active: filters.birthdays, 'filter-birthday': true }"
						@click="toggleFilter('birthdays')"
					>
						<i class="fas fa-birthday-cake"></i> Cumpleaños
					</button>
					<button 
						class="filter-btn" 
						:class="{ active: filters.vacations, 'filter-vacation': true }"
						@click="toggleFilter('vacations')"
					>
						<i class="fas fa-umbrella-beach"></i> Vacaciones
					</button>
					<select 
						v-if="filters.vacations"
						v-model="selectedAreaId" 
						@change="onAreaChange"
						class="area-select"
					>
						<option value="">Todas las áreas</option>
						<option v-for="area in areas" :key="area.id" :value="area.id">{{ area.name }}</option>
					</select>
				</div>
			</div>
			<div class="calendar-body">
				<FullCalendar ref="calendar" :options="calendarOptions" />
			</div>
		</div>
	</div>
</template>

<script>
import '@fullcalendar/core/vdom'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from "@fullcalendar/core/locales/es";
import moment from 'moment'

import request from '../../services/request'
import { mapActions } from 'vuex';

export default {
	name: "calendar-all",
	components: {
		FullCalendar
	},
	data() {
		return {
			filters: {
				anniversaries: true,
				birthdays: true,
				vacations: false
			},
			allEvents: [],
			vacationEvents: [],
			areas: [],
			selectedAreaId: '',
			currentDateRange: null,
			currentParams: null,
			isLoading: false,
			calendarOptions: {
				locale: esLocale,
				plugins: [dayGridPlugin, interactionPlugin],
				initialView: 'dayGridMonth',
				height: 'auto',
				headerToolbar: {
					left: 'prev,next today',
					center: 'title',
					right: ''
				},
				events: [],
				datesSet: this.handleDatesSet
			}
		}
	},
	async mounted() {
		await this.fetchAreas();
	},
	methods: {
		async fetchAreas() {
			try {
				const { data } = await request.get('/catalogue/areas');
				this.areas = data || [];
			} catch (error) {
				console.error("Error fetching areas:", error);
			}
		},
		async handleDatesSet(dateInfo) {
			const start = moment(dateInfo.start).format("YYYY-MM-DD");
			const end = moment(dateInfo.end).format("YYYY-MM-DD");
			const rangeKey = `${start}_${end}`;
			
			if (this.currentDateRange === rangeKey || this.isLoading) {
				return;
			}
			
			this.currentDateRange = rangeKey;
			this.currentParams = { start, end };
			this.isLoading = true;
			
			try {
				await Promise.all([
					this.fetchEvents(this.currentParams),
					this.fetchVacations()
				]);
				this.applyFilters();
			} finally {
				this.isLoading = false;
			}
		},
		async fetchEvents(params) {
			try {
				const { data } = await request.get('/events', params);
				this.allEvents = data || [];
			} catch (error) {
				console.error("Error fetching events:", error);
			}
		},
		async fetchVacations() {
			if (!this.currentParams) return;
			try {
				const params = { ...this.currentParams };
				if (this.selectedAreaId) {
					params.area_id = this.selectedAreaId;
				}
				const { data } = await request.get('/events/vacations', params);
				this.vacationEvents = data || [];
			} catch (error) {
				console.error("Error fetching vacations:", error);
			}
		},
		async onAreaChange() {
			await this.fetchVacations();
			this.applyFilters();
		},
		toggleFilter(filterName) {
			this.filters[filterName] = !this.filters[filterName];
			this.applyFilters();
		},
		applyFilters() {
			let events = [];
			
			if (this.filters.anniversaries) {
				events = events.concat(this.allEvents.filter(e => e.classNames === 'admission_date'));
			}
			if (this.filters.birthdays) {
				events = events.concat(this.allEvents.filter(e => e.classNames === 'birthday'));
			}
			if (this.filters.vacations) {
				events = events.concat(this.vacationEvents);
			}
			
			// Also include project events always
			events = events.concat(this.allEvents.filter(e => 
				e.classNames === 'init_proyect' || e.classNames === 'end_proyect'
			));
			
			this.calendarOptions.events = events;
		},
		...mapActions({
			errorNotification: 'alert/error',
			successNotification: 'alert/success',
		})
	}
}
</script>

<style>
/* ============================
   CALENDAR CONTAINER
   ============================ */
.calendar-container {
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08);
	overflow: hidden;
}

.calendar-header {
	background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
	padding: 1.25rem 1.5rem;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 1rem;
}

.calendar-title {
	color: #ffffff;
	font-size: 1.25rem;
	font-weight: 600;
	margin: 0;
}

.calendar-filters {
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
	align-items: center;
}

.area-select {
	font-size: 0.8rem;
	padding: 0.5rem 1rem;
	border-radius: 20px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	background: rgba(255, 255, 255, 0.95);
	color: #8b5cf6;
	cursor: pointer;
	font-weight: 500;
	outline: none;
}

.filter-btn {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.8rem;
	padding: 0.5rem 1rem;
	border-radius: 20px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	background: rgba(255, 255, 255, 0.1);
	color: rgba(255, 255, 255, 0.7);
	cursor: pointer;
	transition: all 0.2s ease;
}

.filter-btn:hover {
	background: rgba(255, 255, 255, 0.2);
}

.filter-btn.active {
	background: rgba(255, 255, 255, 0.95);
	border-color: transparent;
}

.filter-btn.active.filter-anniversary {
	color: #22c55e;
}

.filter-btn.active.filter-birthday {
	color: #ec4899;
}

.filter-btn.active.filter-vacation {
	color: #8b5cf6;
}

.calendar-body {
	padding: 1rem;
}

/* ============================
   FULLCALENDAR OVERRIDES
   ============================ */
.fc {
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.fc .fc-toolbar-title {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1e293b;
}

.fc .fc-button {
	background: #f1f5f9 !important;
	border: none !important;
	color: #475569 !important;
	font-weight: 500;
	padding: 0.5rem 1rem;
	border-radius: 8px !important;
	box-shadow: none !important;
}

.fc .fc-button:hover {
	background: #e2e8f0 !important;
	color: #0f766e !important;
}

.fc .fc-button-active {
	background: #0f766e !important;
	color: #ffffff !important;
}

.fc .fc-col-header-cell {
	background: #f8fafc;
	border: none !important;
	padding: 0.75rem 0;
}

.fc .fc-col-header-cell-cushion {
	color: #64748b;
	font-weight: 600;
	font-size: 0.75rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.fc .fc-daygrid-day {
	border: 1px solid #f1f5f9 !important;
}

.fc .fc-daygrid-day-number {
	color: #334155;
	font-weight: 500;
	padding: 0.5rem;
}

.fc .fc-day-today {
	background: rgba(20, 184, 166, 0.05) !important;
}

.fc .fc-day-today .fc-daygrid-day-number {
	background: #0f766e;
	color: #ffffff;
	border-radius: 50%;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0.25rem;
}

.fc .fc-day-other .fc-daygrid-day-number {
	opacity: 0.4;
}

/* ============================
   EVENT CARDS
   ============================ */
.fc-event {
	border: none !important;
	border-radius: 6px !important;
	margin: 2px 4px !important;
	padding: 4px 8px !important;
	font-size: 0.75rem !important;
	cursor: pointer;
	transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.fc-event:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.fc-event-title {
	font-weight: 600;
	white-space: normal !important;
}

/* Birthday Events */
.birthday {
	background: linear-gradient(to right, #fdf2f8, #fce7f3) !important;
	border-left: 4px solid #ec4899 !important;
	color: #be185d !important;
}

.birthday .fc-event-title { color: #9d174d; }

.birthday:before {
	font-family: "Font Awesome 5 Free";
	content: "\f1fd";
	font-weight: 900;
	margin-right: 6px;
	color: #ec4899;
}

/* Anniversary Events */
.admission_date {
	background: linear-gradient(to right, #f0fdf4, #dcfce7) !important;
	border-left: 4px solid #22c55e !important;
	color: #166534 !important;
}

.admission_date .fc-event-title { color: #15803d; }

.admission_date:before {
	font-family: "Font Awesome 5 Free";
	content: "\f59c";
	font-weight: 900;
	margin-right: 6px;
	color: #22c55e;
}

/* Vacation Events - continuous bar style */
.vacation_event {
	background: linear-gradient(to right, #ede9fe, #ddd6fe) !important;
	border-left: 4px solid #8b5cf6 !important;
	color: #5b21b6 !important;
	border-radius: 4px !important;
	font-size: 0.7rem !important;
	opacity: 0.9;
}

.vacation_event .fc-event-title { 
	color: #6d28d9; 
	font-weight: 500;
}

.vacation_event:before {
	font-family: "Font Awesome 5 Free";
	content: "\f5ca";
	font-weight: 900;
	margin-right: 6px;
	color: #8b5cf6;
}

/* Project Start */
.init_proyect {
	background: linear-gradient(to right, #eff6ff, #dbeafe) !important;
	border-left: 4px solid #3b82f6 !important;
	color: #1e40af !important;
}

.init_proyect:before {
	font-family: "Font Awesome 5 Free";
	content: "\f164";
	font-weight: 900;
	margin-right: 6px;
	color: #3b82f6;
}

/* Project End */
.end_proyect {
	background: linear-gradient(to right, #fefce8, #fef9c3) !important;
	border-left: 4px solid #eab308 !important;
	color: #854d0e !important;
}

.end_proyect:before {
	font-family: "Font Awesome 5 Free";
	content: "\f2b5";
	font-weight: 900;
	margin-right: 6px;
	color: #eab308;
}
</style>
